/**
 * GymSelectorMap — the Google Maps panel of the gym selector ("/").
 *
 * Mounted only while the map is open, so the map instance, its markers and the
 * `window.__gymSelectorEnter__` bridge live exactly as long as the panel does.
 * (The selector used to null the refs by hand when the panel closed; unmounting
 * does that on its own.)
 *
 * The "Enter" button inside a marker's InfoWindow is the reason for the global:
 * InfoWindow content is a raw HTML string handed to the Maps SDK, not React, so
 * its onclick cannot close over a component callback. The global is the bridge,
 * and it forwards to the `onEnter` prop.
 */
import React, { useEffect, useRef, useState } from "react";
import { F, DEFAULT_ACCENT } from "../../constants";
import { isGymOpen } from "../../utils";
import { useAppTheme } from "../../contexts/ThemeContext";
import { useTranslation } from "../../i18n";
import { loadGoogleMaps } from "../../utils/loadGoogleMaps";

// Drop-pin SVG path for custom markers
const PIN_PATH = "M12 0C5.4 0 0 5.4 0 12c0 7.2 12 22 12 22s12-14.8 12-22C24 5.4 18.6 0 12 0z";

export function GymSelectorMap({ apiKey, gyms, latitude, longitude, onEnter }) {
  const { t } = useTranslation();
  const { th, userTheme } = useAppTheme();
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [mapError, setMapError] = useState(null);

  // Global callback for the InfoWindow "Enter" button. Kept in a ref-free
  // effect so it always points at the current `onEnter` (which closes over the
  // signed-in state), and deleted on unmount with the panel.
  useEffect(() => {
    window.__gymSelectorEnter__ = (slug) => onEnter(slug);
    return () => { delete window.__gymSelectorEnter__; };
  }, [onEnter]);

  // Load Google Maps script directly (bypasses @googlemaps/js-api-loader which sends wrong param name)
  useEffect(() => {
    if (!apiKey || !mapContainerRef.current) return;

    let cancelled = false;
    setMapError(null);

    (async () => {
      try {
        await loadGoogleMaps(apiKey.trim());
        if (cancelled) return;

        const gm = window.google.maps;
        const { Map, InfoWindow } = gm;

        // Create map if not already created
        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new Map(mapContainerRef.current, {
            zoom: 7,
            center: { lat: 47.5, lng: 19.0 },
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            gestureHandling: "cooperative",
          });

          // Custom "My location" button
          if (latitude && longitude) {
            const locBtn = document.createElement("button");
            locBtn.title = t('gymSelector.myLocation');
            locBtn.style.cssText = "background:#fff;border:none;border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.3);cursor:pointer;margin:10px;padding:0;width:40px;height:40px;display:flex;align-items:center;justify-content:center;";
            locBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg>';
            locBtn.addEventListener("click", () => {
              mapInstanceRef.current?.setCenter({ lat: latitude, lng: longitude });
              mapInstanceRef.current?.setZoom(12);
            });
            mapInstanceRef.current.controls[gm.ControlPosition.RIGHT_BOTTOM].push(locBtn);
          }
        }

        // Close any open info window
        if (infoWindowRef.current) infoWindowRef.current.close();

        // Clear old markers
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];

        const bounds = new gm.LatLngBounds();
        let hasValidBounds = false;

        gyms.forEach(gym => {
          if (!gym.location) return;
          const lat = gym.location.latitude || gym.location._lat;
          const lng = gym.location.longitude || gym.location._long;
          if (!lat || !lng) return;

          const pos = { lat, lng };
          bounds.extend(pos);
          hasValidBounds = true;

          const ga = gym.accentColor || DEFAULT_ACCENT;
          const marker = new gm.Marker({
            position: pos,
            map: mapInstanceRef.current,
            title: gym.name,
            icon: {
              path: PIN_PATH,
              scale: 1.4,
              fillColor: ga,
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
              anchor: new gm.Point(12, 34),
              labelOrigin: new gm.Point(12, 11),
            },
            label: {
              text: (gym.name || "S")[0],
              color: "#fff",
              fontWeight: "800",
              fontSize: "12px",
            },
          });

          const openStatus = isGymOpen(gym.openingHours, gym.timezone, gym.specialHours);
          const infoContent = `<div style="font-family:${F};min-width:160px">` +
            `<div style="font-weight:800;font-size:14px;margin-bottom:4px">${gym.name}</div>` +
            (gym.city ? `<div style="font-size:12px;color:#666;margin-bottom:6px">${gym.city}</div>` : "") +
            (openStatus !== null ? `<span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;color:#fff;background:${openStatus === "closing" ? "#f59e0b" : openStatus ? "#16a34a" : "#dc2626"};margin-bottom:6px">${openStatus === "closing" ? t('gymSelector.closingSoon') : openStatus ? t('gymSelector.openNow') : t('gymSelector.closedNow')}</span>` : "") +
            `<div style="display:flex;gap:8px;font-size:12px;color:#666;margin-bottom:8px">` +
              (gym.stats?.activeRoutes > 0 ? `<span>${gym.stats.activeRoutes} ${t('gymSelector.routes')}</span>` : "") +
              (gym.stats?.totalUsers > 0 ? `<span>${gym.stats.totalUsers} ${t('gymSelector.members')}</span>` : "") +
            `</div>` +
            `<button onclick="window.__gymSelectorEnter__('${gym.slug || gym.id}')" style="width:100%;padding:8px;border-radius:8px;border:none;background:${ga};color:#fff;font-weight:700;font-size:13px;cursor:pointer;font-family:${F}">${t('gymSelector.mapEnter')}</button>` +
          `</div>`;

          marker.addListener("click", () => {
            if (infoWindowRef.current) infoWindowRef.current.close();
            const iw = new InfoWindow({ content: infoContent });
            iw.open(mapInstanceRef.current, marker);
            infoWindowRef.current = iw;
          });

          markersRef.current.push(marker);
        });

        // User location blue dot
        if (latitude && longitude) {
          const userPos = { lat: latitude, lng: longitude };
          // Pulsing blue dot — outer ring
          const outerCircle = new gm.Marker({
            position: userPos,
            map: mapInstanceRef.current,
            icon: {
              path: gm.SymbolPath.CIRCLE,
              scale: 14,
              fillColor: "#4285F4",
              fillOpacity: 0.2,
              strokeColor: "#4285F4",
              strokeWeight: 1,
              strokeOpacity: 0.4,
            },
            clickable: false,
            zIndex: 0,
          });
          // Solid blue center
          const innerDot = new gm.Marker({
            position: userPos,
            map: mapInstanceRef.current,
            icon: {
              path: gm.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
            },
            clickable: false,
            zIndex: 1,
          });
          markersRef.current.push(outerCircle, innerDot);
          bounds.extend(userPos);
          hasValidBounds = true;
        }

        // Fit bounds or center on user location
        if (latitude && longitude) {
          mapInstanceRef.current.setCenter({ lat: latitude, lng: longitude });
          mapInstanceRef.current.setZoom(10);
        } else if (hasValidBounds) {
          mapInstanceRef.current.fitBounds(bounds, 40);
        }
      } catch (e) {
        const msg = e?.message || String(e);
        console.error("Google Maps load error:", msg);
        setMapError(msg);
      }
    })();

    return () => { cancelled = true; };
  }, [apiKey, gyms, userTheme, latitude, longitude, t]);

  return (
    <div style={{ position: "relative", marginBottom: 20 }}>
      <div ref={mapContainerRef} style={{
        width: "100%",
        height: window.innerWidth < 768 ? 300 : 400,
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid " + th.border,
      }} />
      {mapError && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", background: th.card,
          borderRadius: 14, border: "1px solid " + th.border,
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🗺️</div>
          <div style={{ fontSize: 13, color: th.textMuted, textAlign: "center", padding: "0 20px", maxWidth: 320 }}>
            {t('gymSelector.mapLoadError')}
          </div>
        </div>
      )}
    </div>
  );
}
